import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import type { Mode, StyleVariant } from '@/constants';
import type { ThemeSlug } from '@/types';

import type { ColorScheme, ThemeMap } from './types';
import { ThemeContext, type ThemeContextValue } from './theme-context';

const MEDIA = '(prefers-color-scheme: dark)';
const THEME_DATA_ATTRIBUTE = 'data-mm-theme';
const VARIANT_DATA_ATTRIBUTE = 'data-mm-style';

// --- Helper Functions ---

const getSystemMode = (): ColorScheme => {
  if (typeof window === 'undefined') {
    return 'light'; // Default for SSR
  }
  return window.matchMedia(MEDIA).matches ? 'dark' : 'light';
};

const getStorageMode = (storageKey: string): Mode | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const storedValue = window.localStorage.getItem(storageKey);
    if (storedValue && ['light', 'dark', 'system'].includes(storedValue)) {
      return storedValue as Mode;
    }
    return null;
  } catch (e) {
    console.error('Error reading localStorage:', e);
    return null;
  }
};

const setStorageMode = (storageKey: string, value: Mode): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    window.localStorage.setItem(storageKey, value);
    return true;
  } catch (e) {
    console.error('Error setting localStorage:', e);
    return false;
  }
};

// Function to temporarily disable animations during theme changes
const disableAnimations = () => {
  if (typeof document === 'undefined') return () => {};

  // Create style element to disable transitions
  const style = document.createElement('style');
  style.appendChild(
    document.createTextNode(
      '*,*::before,*::after{-webkit-transition:none!important;-moz-transition:none!important;-o-transition:none!important;-ms-transition:none!important;transition:none!important}'
    )
  );
  document.head.appendChild(style);

  // Return function to re-enable animations
  return () => {
    // Force a reflow
    window.getComputedStyle(document.body);

    // Remove style element after a short delay
    setTimeout(() => {
      document.head.removeChild(style);
    }, 1);
  };
};

// --- ThemeProvider Props ---

export type ThemeProviderProps = {
  children: React.ReactNode;
  /** Mapping of theme name to HTML attribute value. Key is the theme variant, value is the selected theme slug */
  themes?: ThemeMap;
  /** Forced mode, overrides user preference and system settings */
  forcedMode?: Mode;
  /** Selected style variant */
  variant?: StyleVariant;
  /** Default mode if no preference is stored ('system' by default) */
  defaultMode?: Mode;
  /** Local storage key for storing the mode preference */
  storageKey?: string;
  /** Enable system preference integration */
  enableSystem?: boolean;
  /** Nonce for Content Security Policy */
  nonce?: string;
  /** Disable transitions during theme changes to prevent flash */
  disableTransitionOnChange?: boolean;
  global?: boolean;
};

// --- ThemeProvider Component ---

export const ThemeProvider = (props: ThemeProviderProps) => {
  const {
    children,
    themes = { light: 'light', dark: 'dark' },
    forcedMode,
    variant = 'default',
    defaultMode = 'system',
    storageKey = 'mm-theme-mode',
    enableSystem = true,
    nonce,
    disableTransitionOnChange = true, // Enable by default to prevent FOUC
    global = true,
  } = props;

  // Track if the component has mounted to avoid hydration mismatches
  const [selectedMode, setSelectedMode] = useState<Mode | undefined>(undefined);
  const [resolvedColorScheme, setResolvedColorScheme] = useState<ColorScheme>(
    () =>
      // Initial state determination (server-side or initial client render)
      // Falls back to 'light' if system isn't enabled or window isn't available
      enableSystem ? getSystemMode() : 'light'
  );

  // Determine the mode to use (forced > stored > default)
  // This runs on mount and whenever relevant props/state change
  useEffect(() => {
    const initialStoredMode = getStorageMode(storageKey);
    const modeToUse = forcedMode ?? initialStoredMode ?? defaultMode;
    setSelectedMode(modeToUse);

    if (modeToUse !== 'system' || !enableSystem) {
      setResolvedColorScheme(modeToUse as ColorScheme); // 'light' or 'dark'
    } else {
      // If system, set based on current system preference
      setResolvedColorScheme(getSystemMode());
    }
  }, [forcedMode, storageKey, defaultMode, enableSystem]);

  // Update resolved color scheme when system preference changes (if mode is 'system')
  const handleMediaQuery = useCallback(
    (e: MediaQueryListEvent | MediaQueryList) => {
      // Only react if the effective mode is 'system'
      const currentEffectiveMode = forcedMode ?? selectedMode;
      if (enableSystem && currentEffectiveMode === 'system') {
        const systemScheme = e.matches ? 'dark' : 'light';
        setResolvedColorScheme(systemScheme);
      }
    },
    [selectedMode, forcedMode, enableSystem] // Re-create if these change
  );

  // Add/Remove Media Query Listener
  useEffect(() => {
    if (!enableSystem || typeof window === 'undefined') {
      return;
    }

    const mediaQueryList = window.matchMedia(MEDIA);
    // Initial check in case the state got out of sync before listener attached
    handleMediaQuery(mediaQueryList);

    // Use addEventListener for modern browsers, fall back for older ones
    if (mediaQueryList.addEventListener) {
      mediaQueryList.addEventListener('change', handleMediaQuery);
    } else {
      // Deprecated but needed for Safari < 14
      mediaQueryList.addListener(handleMediaQuery);
    }

    return () => {
      if (mediaQueryList.removeEventListener) {
        mediaQueryList.removeEventListener('change', handleMediaQuery);
      } else {
        // Deprecated but needed for Safari < 14
        mediaQueryList.removeListener(handleMediaQuery);
      }
    };
  }, [handleMediaQuery, enableSystem]); // Run when the handler or enableSystem changes

  // Update mode from storage events (sync across tabs)
  const handleStorage = useCallback(
    (e: StorageEvent) => {
      if (e.key !== storageKey || forcedMode) {
        // Ignore if key doesn't match or if mode is forced
        return;
      }

      const newValue = e.newValue;
      if (newValue && ['light', 'dark', 'system'].includes(newValue)) {
        const newMode = newValue as Mode;
        setSelectedMode(newMode); // Update internal state

        // Update resolved scheme based on the new mode
        if (newMode !== 'system' || !enableSystem) {
          setResolvedColorScheme(newMode as ColorScheme);
        } else {
          setResolvedColorScheme(getSystemMode()); // Re-check system pref
        }
      } else if (newValue === null) {
        // Value was removed, fallback to default
        setSelectedMode(defaultMode);
        if (defaultMode !== 'system' || !enableSystem) {
          setResolvedColorScheme(defaultMode as ColorScheme);
        } else {
          setResolvedColorScheme(getSystemMode());
        }
      }
    },
    [storageKey, forcedMode, defaultMode, enableSystem] // Re-create if these change
  );

  // Add/Remove Storage Listener
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [handleStorage]); // Run only when the handler changes

  // Function for consumers to change the mode
  const setMode = useCallback(
    (newMode: Mode) => {
      if (forcedMode) {
        console.warn(
          'ThemeProvider: Cannot set mode when `forcedMode` is set.'
        );
        return;
      }

      // Disable animations temporarily if requested
      const enableAnimations = disableTransitionOnChange
        ? disableAnimations()
        : null;

      // Persist to storage
      const savedToStorage = setStorageMode(storageKey, newMode);
      if (!savedToStorage) {
        console.warn('ThemeProvider: Failed to save mode to localStorage');
      }

      // Update state
      setSelectedMode(newMode);
      // Update resolved scheme based on the new mode
      if (newMode !== 'system' || !enableSystem) {
        setResolvedColorScheme(newMode as ColorScheme);
      } else {
        setResolvedColorScheme(getSystemMode()); // Check system pref immediately
      }

      // Re-enable animations if they were disabled
      if (enableAnimations) {
        enableAnimations();
      }
    },
    [forcedMode, storageKey, enableSystem, disableTransitionOnChange]
  );

  // Determine the final theme value based on the resolved color scheme
  const finalThemeValue =
    themes[resolvedColorScheme] ??
    (resolvedColorScheme === 'dark' ? 'dark' : 'light');
  const effectiveMode = forcedMode ?? selectedMode ?? defaultMode;

  // Apply attributes to HTML element immediately on mount
  useEffect(() => {
    if (typeof document !== 'undefined' && effectiveMode) {
      // Disable animations temporarily if requested
      const enableAnimations = disableTransitionOnChange
        ? disableAnimations()
        : null;

      const D = document.documentElement;

      // Apply Theme
      D.setAttribute(THEME_DATA_ATTRIBUTE, finalThemeValue);
      // Apply Variant
      D.setAttribute(VARIANT_DATA_ATTRIBUTE, variant);

      // Set color-scheme for browser rendering
      D.style.colorScheme = finalThemeValue.includes('dark') ? 'dark' : 'light';

      if (global) {
        document.body.classList.add('mm-styled');
      } else {
        document.body.classList.remove('mm-styled');
      }

      if (!import.meta.env.PROD) {
        console.log(
          `ThemeProvider: Applied theme='${finalThemeValue}', scheme='${resolvedColorScheme}', variant='${variant}', mode='${effectiveMode}'`
        );
      }

      // Re-enable animations if they were disabled
      if (enableAnimations) {
        enableAnimations();
      }
    }
  }, [
    finalThemeValue,
    resolvedColorScheme,
    variant,
    global,
    effectiveMode,
    disableTransitionOnChange,
  ]);

  // --- Context Value ---
  const contextValue = useMemo(
    (): ThemeContextValue => ({
      mode: effectiveMode,
      colorScheme: resolvedColorScheme,
      theme: finalThemeValue,
      variant,
      setMode,
      themes, // Pass down the available themes map
    }),
    [
      effectiveMode,
      resolvedColorScheme,
      finalThemeValue,
      variant,
      setMode,
      themes,
    ]
  );

  // Provide the context to children
  return (
    <ThemeContext.Provider value={contextValue}>
      {/* Inject script for FOUC prevention - suppressHydrationWarning helps avoid hydration errors */}
      <ThemeScript
        {...{
          suppressHydrationWarning: true,
          theme: finalThemeValue,
          themes,
          mode: effectiveMode,
          variant,
          nonce,
          storageKey,
          enableSystem,
          global,
        }}
      />
      {children}
    </ThemeContext.Provider>
  );
};

// --- Script for FOUC Prevention ---
// This script runs *before* React hydrates to set initial attributes

type ThemeScriptProps = {
  theme?: ThemeSlug;
  themes: ThemeContextValue['themes'];
  mode?: Mode;
  variant?: StyleVariant;
  nonce?: string;
  storageKey: string;
  enableSystem: boolean;
  suppressHydrationWarning?: boolean;
  global?: boolean;
};

const ThemeScript = memo(function ThemeScript({
  theme = 'light',
  themes,
  mode = 'system',
  variant = 'default',
  nonce,
  storageKey,
  enableSystem = true,
  suppressHydrationWarning,
  global,
}: ThemeScriptProps) {
  // Create the script content using a template literal
  const scriptContent = `
(function() {
  try {
    var d = document.documentElement;
    var themeAttr = '${THEME_DATA_ATTRIBUTE}';
    var variantAttr = '${VARIANT_DATA_ATTRIBUTE}';
    var storageKey = '${storageKey}';
    var defaultTheme = '${theme}';
    var defaultMode = '${mode}';
    var enableSystem = ${enableSystem ? 'true' : 'false'};

    // Themes mapping serialized as JSON
    var themesMap = ${JSON.stringify(themes)};

    // Try to get mode from localStorage
    var storedMode;
    try {
      storedMode = localStorage.getItem(storageKey);
    } catch (e) {
      console.warn('Failed to read from localStorage', e);
    }

    // Determine the mode to use
    var effectiveMode = storedMode || defaultMode;

    // Determine the theme based on mode
    var themeToApply = defaultTheme;

    if (effectiveMode === 'light') {
      themeToApply = themesMap.light;
    } else if (effectiveMode === 'dark') {
      themeToApply = themesMap.dark;
    } else if (effectiveMode === 'system' && enableSystem) {
      // Resolve system theme
      var prefersDark = window.matchMedia('${MEDIA}').matches;
      themeToApply = prefersDark ? themesMap.dark : themesMap.light;
    }

    // Apply theme and variant
    d.setAttribute(themeAttr, themeToApply);
    d.setAttribute(variantAttr, '${variant}');

    // Set color-scheme for browser rendering
    d.style.colorScheme = themeToApply.includes('dark') ? 'dark' : 'light';

    ${global ? 'document.body.classList.add("mm-styled")' : ''}
  } catch (e) {
    console.error('Error in ThemeProvider immediate script:', e);
  }
})();
  `;

  // Render the script tag with suppressHydrationWarning
  return (
    <script
      nonce={nonce}
      dangerouslySetInnerHTML={{ __html: scriptContent }}
      suppressHydrationWarning={suppressHydrationWarning}
    />
  );
});
